import React from "react";
import { FiPackage } from "react-icons/fi";
import { MdOutlineWorkspacePremium } from "react-icons/md";
import { RiBarChartBoxLine } from "react-icons/ri";
import { LuBuilding2 } from "react-icons/lu";

export const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    cadence: "/year",
    icon: <FiPackage />,
    accent: "#D6A000",
    iconBg: "#F3F6FA",
    features: ["10 products", "Basic analytics", "Email support"],
  },
  {
    id: "standard",
    name: "Standard",
    price: "$28",
    cadence: "/year",
    icon: <RiBarChartBoxLine />,
    accent: "#3A7BFF",
    iconBg: "#EAF2FF",
    recommended: true,
    features: [
      "50 products",
      "Social ads",
      "Featured seller",
      "Priority support",
      "Advanced analytics",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    price: "TBD",
    cadence: "/year",
    icon: <MdOutlineWorkspacePremium />,
    accent: "#9C6ADE",
    iconBg: "#F5EEFF",
    features: [
      "200 products",
      "Ma.ai assistant",
      "AI descriptions",
      "AI image enhance",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom",
    cadence: "",
    icon: <LuBuilding2 />,
    accent: "#F4A11A",
    iconBg: "#FFF4DE",
    features: [
      "Unlimited",
      "Dedicated manager",
      "Banner featured",
      "Full store managed",
    ],
  },
];

const PLAN_UI_META = PLANS.reduce((acc, plan, index) => {
  acc[plan.id] = {
    icon: plan.icon,
    accent: plan.accent,
    iconBg: plan.iconBg,
    order: index,
  };
  return acc;
}, {});

function formatPrice(plan) {
  const code = String(plan?.code ?? "").toLowerCase();
  const price = Number(plan?.price);

  if (code === "enterprise") return "Custom";
  if (code === "premium" && (!Number.isFinite(price) || price <= 0)) return "TBD";
  if (!Number.isFinite(price)) return "TBD";
  return `$${price}`;
}

function formatCadence(plan) {
  const code = String(plan?.code ?? "").toLowerCase();
  if (code === "enterprise") return "";

  const cycle = String(plan?.billingCycle ?? "").trim();
  return cycle ? `/${cycle}` : "";
}

function buildFeatureList(plan) {
  const code = String(plan?.code ?? "").toLowerCase();
  const features = Array.isArray(plan?.features) ? plan.features : [];
  const productLimit = Number(plan?.productLimit);
  const productFeature = code !== "enterprise" && Number.isFinite(productLimit)
    ? productLimit === 1
      ? "1 product"
      : `${productLimit} products`
    : null;

  return [productFeature, ...features].filter(Boolean);
}

export function mapApiPlanToUiPlan(plan) {
  const code = String(plan?.code ?? "").toLowerCase();
  const fallback = PLANS.find((item) => item.id === code) ?? PLANS[0];
  const meta = PLAN_UI_META[code] ?? PLAN_UI_META[fallback.id];

  return {
    ...fallback,
    id: code || fallback.id,
    planId: plan?._id ?? null,
    name: plan?.name || fallback.name,
    price: formatPrice(plan),
    cadence: formatCadence(plan),
    features: buildFeatureList(plan),
    recommended: Boolean(plan?.isRecommended),
    productLimit: plan?.productLimit ?? fallback.productLimit ?? null,
    raw: plan,
    icon: meta.icon,
    accent: meta.accent,
    iconBg: meta.iconBg,
    order: meta.order,
  };
}

export function buildSelectablePlans(apiPlans) {
  if (!Array.isArray(apiPlans) || apiPlans.length === 0) return PLANS;

  return apiPlans
    .filter((plan) => plan?.isActive !== false)
    .map(mapApiPlanToUiPlan)
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
}

export const EASE = [0.22, 1, 0.36, 1];

export const screenVariants = {
  enter: { opacity: 0, x: 36, scale: 0.985 },
  center: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.42, ease: EASE },
  },
  exit: {
    opacity: 0,
    x: -28,
    scale: 0.985,
    transition: { duration: 0.28, ease: EASE },
  },
};

export const detailCopy = {
  free: {
    title: "You're on the Free plan",
    subtitle: "No card required. Jump straight in.",
    callout:
      "Your free account includes up to 10 product listings, basic analytics (sold, in-stock, out-of-stock), and email support. We'll notify you when you're near your limit.",
    upgradeTitle: "Want more? Upgrade to Standard · $28/yr",
    upgradeBody:
      "Social media ads · Featured seller · 50 products · Priority support · Advanced analytics",
    limitTitle: "What happens at 10 products?",
    limitBody:
      "At 8 products, you'll see an upgrade prompt. At 10 products, new uploads will be blocked until you upgrade or remove an existing listing.",
  },
  standard: {
    title: "Standard gives your store room to grow",
    subtitle: "More listings, better visibility, and priority support.",
    callout:
      "Standard includes 50 product listings, social ads, featured seller placement, advanced analytics, and priority support for active sellers.",
    upgradeTitle: "Built for growing cultural sellers · $28/yr",
    upgradeBody:
      "Use this plan when you're ready to promote more products and get better store insights.",
    limitTitle: "What happens next?",
    limitBody:
      "We'll save your plan choice and route you through the next step. Billing can be connected when payment collection is enabled.",
  },
  premium: {
    title: "Premium is built for AI-assisted selling",
    subtitle: "More automation for larger catalog operations.",
    callout:
      "Premium includes 200 products, Ma.ai assistant support, AI descriptions, and AI image enhancement tools.",
    upgradeTitle: "Premium access is coming soon",
    upgradeBody:
      "Select Premium now to register your interest and continue setting up your store.",
    limitTitle: "What happens next?",
    limitBody:
      "We'll save your plan preference and notify you when Premium billing and AI tools are available.",
  },
  enterprise: {
    title: "Enterprise is tailored to your operation",
    subtitle: "Custom support for managed and high-volume stores.",
    callout:
      "Enterprise includes unlimited listings, a dedicated manager, banner featuring, and managed store support.",
    upgradeTitle: "Custom pricing and onboarding",
    upgradeBody:
      "Select Enterprise to save your preference while we prepare a custom onboarding flow.",
    limitTitle: "What happens next?",
    limitBody:
      "We'll record your plan preference and route you forward. A team-led setup can be connected later.",
  },
};
